'use client'
import React from 'react'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/utilities/ui'

type PropertyAddressProps = {
  className?: string
  wilaya: string | undefined
  city: string | undefined
  addressDetails: {
    address: string
    neighborhood?: string | null
    zip?: string | null
    mapSection: {
      latitudeLongitude?: string | null
      shareLink?: string | null
    }
  }
  dictionary: any
}

const PropertyAddress = ({
  className,
  addressDetails,
  wilaya,
  city,
  dictionary,
}: PropertyAddressProps) => {
  return (
    <div className={cn('bg-card shadow-sm container mx-auto rounded-xl p-[30px]', className)}>
      <h2 className="text-typography text-[17px] font-bold mb-6">
        {dictionary?.propertyDetail?.address || 'Address'}
      </h2>
      <div className="grid grid-cols-1 gap-y-4 gap-x-20 mb-6">
        <div className="grid grid-cols-[140px,1fr] pb-2 border-b border-gray-200">
          <span className="text-sm font-semibold text-typography">
            {dictionary?.propertyDetail?.address || 'Address'}
          </span>
          <span className="text-sm text-descriptionColor">{addressDetails?.address}</span>
        </div>
        <div className="grid grid-cols-[140px,1fr] pb-2 border-b border-gray-200">
          <span className="text-sm font-semibold text-typography">
            {dictionary?.propertyDetail?.zipCode || 'Zip/Postal Code'}
          </span>
          <span className="text-sm text-descriptionColor">{addressDetails?.zip}</span>
        </div>
        <div className="grid grid-cols-[140px,1fr] pb-2 border-b border-gray-200">
          <span className="text-sm font-semibold text-typography">
            {dictionary?.propertyDetail?.city || 'City'}
          </span>
          <span className="text-sm text-descriptionColor">{city}</span>
        </div>
        <div className="grid grid-cols-[140px,1fr] pb-2 border-b border-gray-200">
          <span className="text-sm font-semibold text-typography">
            {dictionary?.propertyDetail?.area || 'Area'}
          </span>
          <span className="text-sm text-descriptionColor">{addressDetails?.neighborhood}</span>
        </div>
        <div className="grid grid-cols-[140px,1fr] pb-2 border-b border-gray-200">
          <span className="text-sm font-semibold text-typography">
            {dictionary?.propertyDetail?.stateCounty || 'State/County'}
          </span>
          <span className="text-sm text-descriptionColor">{wilaya}</span>
        </div>
      </div>
      {/* Map Section */}
      {addressDetails?.mapSection?.latitudeLongitude && (
        <div className="relative rounded-lg overflow-hidden h-64 w-full">
          {/* Embedded Google Map */}
          <iframe
            src={`https://www.google.com/maps?q=${addressDetails?.mapSection?.latitudeLongitude}&z=15&output=embed`}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen={false}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title={dictionary?.propertyDetail?.locationMap || 'Property Location Map'}
            className="absolute inset-0"
          ></iframe>
          {/* Google Maps Link Button */}
          {!!addressDetails?.mapSection?.shareLink && (
            <div className="absolute top-3 right-3">
              <a
                href={addressDetails?.mapSection?.shareLink}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white hover:bg-gray-100 transition-colors text-black px-4 py-2 rounded-md shadow-md flex items-center font-medium text-sm"
              >
                {dictionary?.propertyDetail?.openOnGoogleMaps || 'Open on Google Maps'}
                <ArrowUpRight size={16} className="ml-1" />
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default PropertyAddress
